import { useState, useEffect } from 'react';
import { imageUrlService, ImageContext } from '@/services/ImageUrlService';
import { debugLogger } from '@/utils/debugLogger';

/**
 * useImageUrl Hook
 * 
 * Custom hook for async image URL processing with ImageUrlService
 * Handles loading state and automatic updates when base URL changes
 * 
 * Usage:
 * ```typescript
 * const { url: avatarUrl, isLoading } = useImageUrl(profileData?.main_photo_url, 'avatar');
 * 
 * <AvatarImage src={avatarUrl} alt="User Avatar" />
 * ```
 * 
 * @param baseUrl - Base image URL (can be undefined)
 * @param context - Image context for variant selection
 * @returns Object with processed URL and loading state
 */
export const useImageUrl = (
  baseUrl: string | undefined | null,
  context: ImageContext
): { url: string | undefined; isLoading: boolean } => {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Reset if no base URL
    if (!baseUrl) {
      setUrl(undefined);
      setIsLoading(false);
      return;
    }

    // Set loading state
    setIsLoading(true);

    // Process URL asynchronously
    imageUrlService
      .getImageUrlAsync(baseUrl, context)
      .then(processedUrl => {
        setUrl(processedUrl);
        setIsLoading(false);
        debugLogger.debug('useImageUrl', 'URL processed', {
          baseUrl: baseUrl.substring(0, 100),
          context,
          processedUrl: processedUrl?.substring(0, 100)
        });
      })
      .catch(error => {
        debugLogger.error('useImageUrl', 'Error processing URL', {
          baseUrl: baseUrl.substring(0, 100),
          context,
          error
        });
        // Fallback to original URL on error
        setUrl(baseUrl);
        setIsLoading(false);
      });
  }, [baseUrl, context]);

  return { url, isLoading };
};

/**
 * useImageUrls Hook (plural)
 * 
 * Process multiple image URLs simultaneously
 * 
 * Usage:
 * ```typescript
 * const thumbnailUrls = useImageUrls(photos, 'thumbnail');
 * ```
 */
export const useImageUrls = (
  baseUrls: (string | undefined | null)[],
  context: ImageContext
): (string | undefined)[] => {
  const [urls, setUrls] = useState<(string | undefined)[]>([]);

  useEffect(() => {
    const processUrls = async () => {
      const processed: (string | undefined)[] = [];
      
      for (const url of baseUrls) {
        if (!url) {
          processed.push(undefined);
          continue;
        }
        
        try {
          const result = await imageUrlService.getImageUrlAsync(url, context);
          processed.push(result);
        } catch (error) {
          debugLogger.error('useImageUrls', 'Error processing URL', { url, context, error });
          processed.push(url); // Fallback to original
        }
      }
      
      setUrls(processed);
    };

    processUrls();
    // Use join instead of JSON.stringify for stable dependency comparison
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrls.join('|'), context]);

  return urls;
};
